// @ts-nocheck
import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Autocomplete,
  Box,
  Divider,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { EMPTY_ARRAY } from '../../const/CommonConst';

import { ReactComponent as DropdownArrow } from '../../assets/images/DropdownArrow.svg';

/**
 * Custom Paper component that includes a section filter at the top
 */
const CustomPaper = ({
  children,
  sections,
  selectedSection,
  onSectionChange,
  loading,
  showSectionFilter,
}) => {
  const handleInteraction = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const getOptionLabel = useCallback((option) => option?.label || '', []);
  const renderSectionInput = useCallback(
    (params) => (
      <TextField
        {...params}
        placeholder="All sections"
        size="small"
        onMouseDown={handleInteraction}
        onClick={handleInteraction}
        sx={{
          marginTop: 0,
          '& .MuiInputBase-input': {
            fontSize: '14px',
          },
        }}
      />
    ),
    [handleInteraction]
  );

  const isOptionEqualToValue = useCallback(
    (option, val) => option?.value === val?.value,
    []
  );

  const renderSectionOption = useCallback(
    (props, option) => (
      <Box
        component="li"
        {...props}
        key={option?.value || option?.label}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          gap: 1,
        }}
        title={option?.label}
      >
        <Typography
          variant="body2"
          noWrap
          sx={{
            flex: 1,
          }}
        >
          {option?.label}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            flexShrink: 0,
          }}
        >
          {option?.count || 0}
        </Typography>
      </Box>
    ),
    []
  );

  if (!showSectionFilter) {
    return (
      <Paper elevation={8} onMouseDown={handleInteraction}>
        {children}
      </Paper>
    );
  }

  return (
    <Paper elevation={8} onMouseDown={handleInteraction}>
      <Box sx={{ p: 1.5, width: '100%', position: 'relative' }}>
        <Autocomplete
          size="small"
          options={sections}
          value={selectedSection}
          onChange={onSectionChange}
          isOptionEqualToValue={isOptionEqualToValue}
          getOptionLabel={getOptionLabel}
          popupIcon={<DropdownArrow />}
          disabled={loading}
          disablePortal={false}
          renderInput={renderSectionInput}
          renderOption={renderSectionOption}
          slotProps={{
            listbox: {
              style: {
                overflowX: 'hidden',
              },
            },

            popper: {
              placement: 'bottom-start',
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, 4],
                  },
                },
                {
                  name: 'preventOverflow',
                  options: {
                    boundary: 'viewport',
                    padding: 8,
                  },
                },
              ],
            },
            paper: {
              onMouseDown: handleInteraction,
              onClick: handleInteraction,
              onMouseUp: handleInteraction,
            },
          }}
          sx={{
            width: '100%',
            '& .MuiAutocomplete-popper': {
              width: '100% !important',
            },
          }}
        />
      </Box>
      <Divider />
      <Box onMouseDown={handleInteraction}>{children}</Box>
    </Paper>
  );
};

CustomPaper.propTypes = {
  children: PropTypes.node,
  sections: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
      count: PropTypes.number,
    })
  ).isRequired,
  selectedSection: PropTypes.shape({
    label: PropTypes.string,
    value: PropTypes.string,
  }),
  onSectionChange: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  showSectionFilter: PropTypes.bool,
};

/**
 * SectionAttributePopover - is a unified autocomplete component with section filtering
 *
 * It provides:
 * - Section-based filtering with a dropdown in the popper
 * - Support for attributes with group_name and group_id
 * - Support for "Basic Details" section
 * - All standard MUI Autocomplete props supported
 */
const SectionAttributePopover = ({
  options = EMPTY_ARRAY,
  value,
  onChange,
  getOptionLabel,
  renderOption,
  renderInput,
  loading = false,
  disabled = false,
  label = 'Select Attribute',
  placeholder = 'Select attribute',
  error = false,
  helperText = '',
  required = false,
  sx = {},
  enableSectionFilter = true,
  ...otherProps
}) => {
  const [selectedSection, setSelectedSection] = useState(() => ({
    label: 'All Sections',
    value: 'all',
    count: options?.length || 0,
  }));

  const sections = useMemo(() => {
    const totalCount = options?.length || 0;

    if (!enableSectionFilter || !options?.length) {
      return [{ label: 'All Sections', value: 'all', count: totalCount }];
    }

    const uniqueSections = new Map();

    options.forEach((option) => {
      const groupId = option?.group_id;
      const groupName = option?.group_name;

      if (groupId) {
        if (!uniqueSections.has(groupId)) {
          uniqueSections.set(groupId, {
            label: groupName || `Section ${groupId}`,
            value: groupId,
            count: 0,
          });
        }
        uniqueSections.get(groupId).count += 1;
      }
    });

    return [
      { label: 'All Sections', value: 'all', count: totalCount },
      ...Array.from(uniqueSections.values()),
    ];
  }, [options, enableSectionFilter]);

  const filteredOptions = useMemo(() => {
    if (
      !enableSectionFilter ||
      !selectedSection ||
      selectedSection.value === 'all'
    ) {
      return options;
    }

    return options.filter(
      (option) => option?.group_id === selectedSection.value
    );
  }, [options, selectedSection, enableSectionFilter]);

  const handleSectionChange = useCallback(
    (_, newValue) => {
      setSelectedSection(
        newValue || {
          label: 'All Sections',
          value: 'all',
          count: options?.length || 0,
        }
      );
    },
    [options]
  );

  const defaultGetOptionLabel = useCallback((option) => {
    return option?.label || '';
  }, []);

  const defaultRenderOption = useCallback(
    (props, option) => (
      <Box component="li" {...props} key={option?.id || option?.value}>
        <Typography variant="body2" noWrap>
          {option?.label} {option?.count ? `(${option?.count})` : ''}
        </Typography>
      </Box>
    ),
    []
  );

  const defaultRenderInput = useCallback(
    (params) => (
      <TextField
        {...params}
        label={label}
        placeholder={placeholder}
        error={error}
        helperText={helperText}
        required={required}
        slotProps={{
          ...params.slotProps,
          inputLabel: { ...params.slotProps.inputLabel, shrink: true },
        }}
      />
    ),
    [label, placeholder, error, helperText, required]
  );

  const PaperComponent = useCallback(
    (props) => (
      <CustomPaper
        {...props}
        sections={sections}
        selectedSection={selectedSection}
        onSectionChange={handleSectionChange}
        loading={loading}
        showSectionFilter={enableSectionFilter && sections.length > 1}
      />
    ),
    [
      sections,
      selectedSection,
      handleSectionChange,
      loading,
      enableSectionFilter,
    ]
  );

  const handleRenderInputField = useCallback(
    (params) => (
      <TextField
        {...params}
        slotProps={{
          ...params.slotProps,
          inputLabel: { ...params?.slotProps?.inputLabel, shrink: true },
        }}
        label={label}
        placeholder={placeholder}
        spellCheck={true}
      />
    ),
    [label, placeholder]
  );

  const handleRenderOptions = useCallback(
    (props, option) => (
      <Box
        {...props}
        key={option?.value}
        title={`${option?.label}`}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 4,
        }}
      >
        <Typography
          variant="body2"
          noWrap
          sx={{
            mb: 0.2,
            width: 300,
          }}
        >
          {`${option?.id} : ${option?.label}`}
        </Typography>
      </Box>
    ),
    []
  );

  const isOptionEqualToValue = useCallback(
    (option, val) => option?.id === val?.id || option?.name === val?.name,
    []
  );

  const searchFilter = useCallback((optionsList, state) => {
    const query = state.inputValue.toLowerCase();

    return optionsList.filter((option) => {
      const labelMatch = option.label.toLowerCase().includes(query);
      const idMatch = option.id && option.id.toString().includes(query);
      return labelMatch || idMatch;
    });
  }, []);
  return (
    <Autocomplete
      fullWidth
      options={filteredOptions}
      value={value}
      onChange={onChange}
      getOptionLabel={getOptionLabel || defaultGetOptionLabel}
      renderOption={renderOption || handleRenderOptions || defaultRenderOption}
      renderInput={renderInput || handleRenderInputField || defaultRenderInput}
      disabled={disabled}
      loading={loading}
      popupIcon={<DropdownArrow />}
      slots={{
        paper: PaperComponent,
      }}
      isOptionEqualToValue={isOptionEqualToValue}
      slotProps={{
        listbox: {
          sx: {
            maxHeight: '250px',
            '& .MuiAutocomplete-option': {
              fontSize: '14px',
            },
          },
        },

        popper: {
          placement: 'bottom-start',
          modifiers: [
            {
              name: 'flip',
              enabled: true,
            },
            {
              name: 'preventOverflow',
              enabled: true,
              options: {
                altAxis: true,
                boundary: 'viewport',
                padding: 8,
              },
            },
          ],
        },
      }}
      sx={{
        ...sx,
      }}
      filterOptions={searchFilter}
      {...otherProps}
    />
  );
};

SectionAttributePopover.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    })
  ).isRequired,
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  onChange: PropTypes.func.isRequired,
  getOptionLabel: PropTypes.func,
  renderOption: PropTypes.func,
  renderInput: PropTypes.func,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  error: PropTypes.bool,
  helperText: PropTypes.string,
  required: PropTypes.bool,
  sx: PropTypes.shape({}),
  enableSectionFilter: PropTypes.bool,
};

export default SectionAttributePopover;
